import { useCallback, useRef } from 'react'
import { ObjectSchema } from 'yup'
import {
  useForm as useFormOriginal,
  // Types
  type UseFormProps,
  type UseFormHandleSubmit,
  type SubmitHandler,
  type Resolver,
} from 'react-hook-form'
import { FieldValues, UseFormReturn } from 'react-hook-form/dist/types'
import { yupResolver } from '@hookform/resolvers/yup'

export type UseJourneyFormReturn<TFieldValues extends FieldValues, TContext = any> = UseFormReturn<
  TFieldValues,
  TContext
> & {
  handleIncomingData: (data: TFieldValues) => void
}

export type UseJourneyFormProps<TFieldValues extends FieldValues, TContext = any> = UseFormProps<
  TFieldValues,
  TContext
> & {
  resetOnSubmit?: boolean
  validationSchema: ObjectSchema<TFieldValues>
}

/**
 * `useJourneyForm` hook adds `resetOnSubmit` and `handleIncomingData` to the `useForm` hook; meant to be used for forms editable by multiple users at once
 *
 * **LIMITATIONS**
 * - Only supports 1-dimensional data structure
 */
export const useJourneyForm = <TFieldValues extends FieldValues, TContext = any>({
  validationSchema,
  resetOnSubmit = true,
  ...props
}: UseJourneyFormProps<TFieldValues, TContext>): UseJourneyFormReturn<TFieldValues, TContext> => {
  const {
    formState,
    reset,
    handleSubmit: originalHandleSubmit,
    ...restForm
  } = useFormOriginal<TFieldValues, TContext>({
    ...props,
    resolver: yupResolver(validationSchema.required()) as unknown as Resolver<TFieldValues>,
  })

  // A wrapper for handleSubmit that inherently resets the isDirty state to false
  // to allow for better comparisons
  const handleSubmit: UseFormHandleSubmit<TFieldValues> = useCallback(
    (onSubmit) => {
      if (!resetOnSubmit) return originalHandleSubmit(onSubmit)

      const wrappedOnSubmit: SubmitHandler<TFieldValues> = (data, event) => {
        onSubmit(data, event)
        reset({} as any, { keepValues: true })
      }

      return originalHandleSubmit(wrappedOnSubmit)
    },
    [originalHandleSubmit, reset, resetOnSubmit],
  )

  // Use handleIncomingData to update the form values when the data changes
  // instead of setValue or reset
  const handleIncomingData = useCallback(
    (data: TFieldValues) => {
      const fieldValues = restForm.getValues()

      Object.keys(data).forEach((inputName) => {
        const { dirtyFields } = formState
        const isDirty = (dirtyFields as any)[inputName] ?? false
        const isDifferent = fieldValues[inputName] !== data[inputName]

        if (!isDirty && isDifferent) {
          restForm.setValue<any>(inputName, data[inputName])
        }
      })
    },
    [formState, restForm],
  )

  const handleIncomingDataRef = useRef(handleIncomingData)
  handleIncomingDataRef.current = handleIncomingData

  // Wrapper with calling ref object helps to avoid re-rendering
  const handleIncomingDataWrapper = useCallback(
    (data: TFieldValues) => handleIncomingDataRef.current(data),
    [],
  )

  return {
    formState,
    handleIncomingData: handleIncomingDataWrapper,
    handleSubmit,
    reset,
    ...restForm,
  } as const
}
